import React, { useRef, useEffect } from "react";

const VerticalSelector = ({
  options,
  selectedIndex,
  setSelectedIndex,
  labelKey = "label",
  iconKey = "icon",
  itemHeight = 48,
  visibleCount = 5,
}) => {
  const containerRef = useRef(null);
  const animationFrame = useRef(null);
  const isUserScrolling = useRef(false);
  const snapTimeout = useRef(null);
  const scrollTimeout = useRef(null);

  const paddingCount = Math.floor(visibleCount / 2);

  const scrollToIndex = (index, fromClick = false) => {
    if (!containerRef.current) return;
    const targetTop = itemHeight * index;

    if (fromClick) {
      setSelectedIndex(index);
      isUserScrolling.current = true;
      containerRef.current.scrollTo({ top: targetTop, behavior: "smooth" });
      setTimeout(() => (isUserScrolling.current = false), 300);
    } else {
      const currentTop = containerRef.current.scrollTop;
      if (Math.abs(targetTop - currentTop) > 5) {
        containerRef.current.scrollTo({ top: targetTop, behavior: "smooth" });
      }
    }
  };

  const scheduleSnap = () => {
    clearTimeout(snapTimeout.current);
    snapTimeout.current = setTimeout(() => {
      if (!isUserScrolling.current && containerRef.current) {
        const rawIndex = containerRef.current.scrollTop / itemHeight;
        const targetIndex = Math.max(
          0,
          Math.min(options.length - 1, Math.round(rawIndex))
        );
        if (targetIndex !== selectedIndex) {
          setSelectedIndex(targetIndex);
          scrollToIndex(targetIndex);
        }
      }
    }, 100);
  };

  const handleScroll = () => {
    if (!containerRef.current) return;
    isUserScrolling.current = true;

    if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    clearTimeout(snapTimeout.current);
    clearTimeout(scrollTimeout.current);

    animationFrame.current = requestAnimationFrame(() => {
      const scrollTop = containerRef.current.scrollTop;
      const rawIndex = scrollTop / itemHeight;
      const nearestIndex = Math.max(
        0,
        Math.min(options.length - 1, Math.round(rawIndex))
      );

      if (nearestIndex !== selectedIndex) {
        setSelectedIndex(nearestIndex);
      }
    });

    scrollTimeout.current = setTimeout(() => {
      isUserScrolling.current = false;
      scheduleSnap();
    }, 150);
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = itemHeight * selectedIndex;
    }
  }, [itemHeight, selectedIndex]);

  useEffect(() => {
    return () => {
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
      clearTimeout(snapTimeout.current);
      clearTimeout(scrollTimeout.current);
    };
  }, []);

  return (
    <div className="relative h-60 overflow-hidden">
      {/* 可选：左右指示器 */}
      <div className="absolute top-1/2 left-2 -translate-y-1/2 text-gray-500 pointer-events-none z-10">≫</div>
      <div className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-500 pointer-events-none z-10">≪</div>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-full overflow-y-scroll overflow-x-hidden scrollbar-none"
        style={{
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
        }}
      >
        <style jsx>{`
          .scrollbar-none::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-none {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

        {Array.from({ length: paddingCount }).map((_, i) => (
          <div key={`top-${i}`} className="h-12" />
        ))}

        {options.map((option, index) => {
          const isSelected = index === selectedIndex;
          const distance = Math.abs(index - selectedIndex);
          const opacity = Math.max(0.3, 1 - distance * 0.2);
          const scale = isSelected ? 1.1 : Math.max(0.85, 1 - distance * 0.05);

          return (
            <div
              key={index}
              onClick={() => scrollToIndex(index, true)}
              className="h-12 flex items-center justify-center cursor-pointer transition-all duration-150 ease-out"
              style={{ transform: `scale(${scale})`, opacity }}
            >
              <div className="flex items-center space-x-2">
                {iconKey && option[iconKey] && (
                  <span className="text-2xl" style={{ filter: isSelected ? "none" : "grayscale(0.3)" }}>
                    {option[iconKey]}
                  </span>
                )}
                <span className={`text-base font-medium ${isSelected ? "text-black" : "text-gray-500"}`}>
                  {option[labelKey]}
                </span>
              </div>
            </div>
          );
        })}

        {Array.from({ length: paddingCount }).map((_, i) => (
          <div key={`bottom-${i}`} className="h-12" />
        ))}
      </div>
    </div>
  );
};

export default VerticalSelector;